import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import "./Alert.css";

export type AlertTone = "success" | "attention" | "warning" | "error" | "danger" | "info";

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: AlertTone;
  title?: ReactNode;
  icon?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

const DefaultIcon = ({ tone }: { tone: AlertTone }) => {
  const path = (() => {
    switch (tone) {
      case "success":
        return (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12.5l3 3 5-6" />
          </>
        );
      case "error":
      case "danger":
        return (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 7v6" />
            <path d="M12 16.5h.01" />
          </>
        );
      case "attention":
      case "warning":
        return (
          <>
            <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            <path d="M12 8v3" />
            <path d="M12 14h.01" />
          </>
        );
      case "info":
      default:
        return (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 11v6" />
            <path d="M12 7h.01" />
          </>
        );
    }
  })();
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    tone = "info",
    title,
    icon,
    onDismiss,
    dismissLabel = "Dismiss",
    className,
    children,
    role,
    ...rest
  },
  ref,
) {
  const resolvedRole = role ?? (tone === "error" || tone === "danger" ? "alert" : "status");
  return (
    <div
      ref={ref}
      role={resolvedRole}
      className={cx("go4-alert", `go4-alert--${tone}`, className)}
      {...rest}
    >
      <span className="go4-alert__icon">{icon ?? <DefaultIcon tone={tone} />}</span>
      <div className="go4-alert__content">
        {title ? <div className="go4-alert__title">{title}</div> : null}
        {children ? <div className="go4-alert__message">{children}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          aria-label={dismissLabel}
          className="go4-alert__close"
          onClick={onDismiss}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      ) : null}
    </div>
  );
});
